import { useMemo, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";

import { useCompanyContext } from "Bands/contexts";
import {
  cancelSubscription,
  dataMetaSelector,
  dispatchGetBandAction,
  dispatchGetSubscriptionsAction,
  getCompanySubscriptions,
} from "Bands/state";
import { BAND_ACTIONS, IBandSubscription, SubscriptionStatus } from "Bands/types";
import { useClearActionMetaOnUnMount } from "Bands/utils";
import { copy } from "shared/assets";
import { Company } from "shared/assets/icons";
import {
  ButtonContainer,
  Message,
  Modal,
  Button,
  Loader,
  APIErrorMessage,
} from "shared/components";
import { addTemporaryToast } from "shared/components/Toast";
import { Spacer } from "shared/layout";
import { BoldText } from "shared/styles";
import { IAPIState, IStatus, ITheme } from "shared/types";
import { formatDateTime } from "shared/utils";

const modalCopy = copy.subscription.cancel;

interface CancelSubscriptionModalProps {
  subscriptionId?: string;
  subscription?: IBandSubscription;
  handleClose: () => void;
  refetchCompany?: boolean;
  refetchBand?: boolean;
}

export const CancelSubscriptionModal = ({
  subscriptionId,
  subscription,
  handleClose,
  refetchCompany,
  refetchBand,
}: CancelSubscriptionModalProps) => {
  const dispatch = useDispatch();
  const { companySubscriptions, companyPath } = useCompanyContext();
  const { api } = useSelector(dataMetaSelector(BAND_ACTIONS.cancelBandSubscription));

  useClearActionMetaOnUnMount(BAND_ACTIONS.cancelBandSubscription);

  const selectedSubscription = useMemo(() => {
    if (subscription) return subscription;

    return companySubscriptions.find(({ id }) => id === subscriptionId);
  }, [subscription, subscriptionId, companySubscriptions]);

  useEffect(() => {
    if (api?.status === IAPIState.succeeded) {
      dispatch(
        addTemporaryToast({
          status: IStatus.success,
          message: modalCopy.success,
        })
      );

      if (refetchCompany && companyPath) {
        dispatch(
          getCompanySubscriptions({
            company: companyPath,
            status: SubscriptionStatus.active,
          })
        );
      }

      const bandId = selectedSubscription?.bands[0].id;
      if (refetchBand && bandId) {
        // refetch band to update subscription
        dispatchGetSubscriptionsAction(dispatch, {
          band: bandId,
        });

        // refetch band to update band info
        dispatchGetBandAction(dispatch, bandId);
      }

      handleClose();
    }
  }, [
    api?.status,
    companyPath,
    dispatch,
    handleClose,
    refetchBand,
    refetchCompany,
    selectedSubscription,
  ]);

  const handleSubmit = () => {
    if (!selectedSubscription) return;

    dispatch(cancelSubscription(selectedSubscription.id));
  };

  const message = useMemo(() => {
    if (!selectedSubscription) return null;

    const { plan, bands, nextBillingDate } = selectedSubscription;

    return (
      <>
        <span>{modalCopy.text[0]}</span>
        <BoldText>
          {plan}
          {modalCopy.text[1]}
        </BoldText>
        <span>{modalCopy.text[2]}</span>
        <BoldText>{bands[0]?.title}</BoldText>
        {nextBillingDate && (
          <>
            <span>{modalCopy.text[3]}</span>
            <BoldText>
              {formatDateTime(nextBillingDate, {
                dateOnly: true,
              })}
            </BoldText>
          </>
        )}
      </>
    );
  }, [selectedSubscription]);

  return (
    <Modal title={modalCopy.title} icon={Company} handleClose={handleClose}>
      <Spacer size="m">
        <Message status={IStatus.warning}>{message}</Message>
        <ButtonContainer>
          <Button
            status={IStatus.error}
            onClick={handleSubmit}
            data-testid="cancel_subscription_button"
          >
            <span>{modalCopy.buttons.submit}</span>
            {api?.status === IAPIState.pending && <Loader size="s" />}
          </Button>
          <Button theme={ITheme.secondary} onClick={handleClose}>
            {modalCopy.buttons.cancel}
          </Button>
        </ButtonContainer>
        {api?.status === IAPIState.failed && (
          <APIErrorMessage apiErrorMessage={true} apiError={api?.error} />
        )}
      </Spacer>
    </Modal>
  );
};
